<template>
  <v-row no-gutters class="fill-height">
    <v-col cols="12">
      <main-loader v-if="isLoading" class="pt-10" />
      <template v-else>
        <card-title-row
          is-floating
          :feature-title="title"
          :has-back-button="true"
          :has-icon-column="true"
          :has-tools-buttons="true"
          @action="goBack"
        >
          <template #toolsButtons>
            <v-row>
              <feature-card-zdm-buttons
                :permissions="extrasPermission"
                :geometry="geometry"
                @external_systems="openZSIDialog"
                :layer-id="streetLampsLayerId"
                :dataSource="dataSource"
                :feature-id="featureId"
                :number-attribute-value="numberAttributeValue"
                :street-attribute-value="streetAttributeValue"
                :district-attribute-value="districtAttributeValue"
              ></feature-card-zdm-buttons>
            </v-row>
          </template>
        </card-title-row>
        <sidebar-row class="pt-8 pb-5 pr-5" :border-bottom="true">
          <lamp-treeview
            :is-editing-on="isEditingOn"
            @edit="startEditing"
            @delete="onDelete"
            @addSubfeature="openAddDialog"
            :featureId="featureId"
            :structure="treeviewStructure"
            v-model="activeFeature"
            :streetLampDatasource="zdmStreetLampDataSourceName"
          ></lamp-treeview>
        </sidebar-row>
        <template v-if="isLightingControlModuleEnabled">
          <sidebar-row v-if="type === 'head'" class="pr-5" :border-bottom="true">
            <zdm-lighting-control-widget :featureId="routeQuery.id" :values="values" class="pb-5" />
          </sidebar-row>
        </template>
        <sidebar-row v-if="type === MAIN_TYPE" class="pr-5" :border-bottom="true">
          <zdm-relation-widget :data-source="zdmStreetLampDataSourceName" :featureId="featureId" class="pb-2" />
        </sidebar-row>
        <object-extras
          ref="extras"
          :are-extras-hidden="isNew"
          :layerId="layerId"
          :featureProps="feature.properties"
          :featureId="featureId"
          :subFeatureId="subFeatureId"
          :dataSourceName="dataSource"
          :subDataSourceName="subFeatureDataSourceName"
          :permissions="extrasPermission"
          :geometry="geometry"
        >
          <template #attributes>
            <v-form v-model="isFormValid">
              <sidebar-form-row
                v-if="type !== MAIN_TYPE && typeAttribute"
                :is-editing-on="isEditingOn"
                v-bind="typeAttribute"
                v-model="values[typeAttribute.name]"
                class="pt-5"
              >
              </sidebar-form-row>
              <feature-card-data
                :elements="formSchema"
                v-model="values"
                :is-editing-on="isEditingOn"
                :layer-id="subFeatureLayerId"
                arrows-mode="on-with-storage"
                :dense="true"
                :outlined="true"
                align-class="align-baseline"
              />
            </v-form>
          </template>
        </object-extras>
      </template>
      <ExternalSystemsDialog
        :is-visible.sync="isZSIDialogVisible"
        v-if="isZSIDialogLoaded"
        :feature-id="featureId"
        :system-id="datasourceSystemId"
        :datasource="dataSource"
      />
      <main-dialog
        :is-visible.sync="isDeleteDialogVisible"
        v-if="isDeleteDialogLoaded"
        :title="deleteDialogTitle"
        :body="deleteDialogBody"
        agree-text="dialog.agreeDelete"
        @agree="deleteObjectHandler"
        :loading="agreeDeleteButtonLoading"
      ></main-dialog>
      <main-input-dialog
        v-if="isAddSubfeatureDialogLoaded"
        :is-visible.sync="isAddSubfeatureDialogVisible"
        :title-translation="addSubfeatureDialogTitle"
        agree-text="dialog.agreeAdd"
        :is-input-dialog="true"
        @agree="handleSubfeatureAdding"
        :inputs="[addedObjectInputConfig]"
        :loading="addSubfeatureButtonLoading"
      >
        <template #type.input="slot">
          <v-row no-gutters>
            <v-col>
              <data-input v-bind="slot" v-model="addedObject.type" clearable> </data-input>
            </v-col>
          </v-row>
        </template>
      </main-input-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';
import { cloneDeep } from 'lodash';

import { prepareDrawnGeometry } from '@/assets/js/mapUtils';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import fetchAttributesSchemaMixin from '@/mixins/fetchAttributesSchemaMixin';
import featureCardHighlight from '@/mixins/featureCardHighlight';
import featureCardZdmButtonsMixin from '@/mixins/featureCardZdmButtonsMixin';

import LampTreeview from '@/components/LampTreeview';
import CardTitleRow from '@/components/CardTitleRow';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import ObjectExtras from '@/components/ObjectExtras';
import FeatureCardData from '@/components/FeatureCardData';
import FeatureCardZdmButtons from '@/components/FeatureCardZdmButtons';
import SidebarFormRow from '@/components/SidebarFormRow';
import DataInput from '@/components/DataInput';
import ZdmRelationWidget from '@/components/ZdmRelationWidget';
import ZdmLightingControlWidget from '@/components/ZdmLightingControlWidget';

const MAIN_TYPE = 'street_lamp';

export default {
  name: 'FeatureCardZdmLamp',
  mixins: [
    featureCardHighlight,
    fetchAttributesSchemaMixin,
    prepareAttributesFeatureCardMixin,
    featureCardZdmButtonsMixin,
  ],
  components: {
    ExternalSystemsDialog: () => import('@/components/ExternalSystemsDialog'),
    LampTreeview,
    CardTitleRow,
    MainLoader,
    SidebarRow,
    ObjectExtras,
    FeatureCardData,
    FeatureCardZdmButtons,
    SidebarFormRow,
    DataInput,
    ZdmRelationWidget,
    ZdmLightingControlWidget,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    dataSource: {
      type: String,
      required: true,
    },
    layer: {
      type: Object,
      required: true,
    },
    lampId: {
      type: [String, Number],
      default: null,
    },
    isSubfeature: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    MAIN_TYPE,
    subFeaturesNamesDict: {
      street_lamp: 'pole',
      pole: 'pick_arm',
      pick_arm: 'head',
    },
    isZSIDialogVisible: false,
    isZSIDialogLoaded: false,
    feature: undefined,
    isLoading: true,
    isNew: false,
    formSchema: undefined,
    values: {},
    isFormValid: true,
    isEditingOn: false,
    subfeaturesDict: undefined,
    treeviewStructure: undefined,
    activeFeature: undefined,
    type: undefined,
    typeAttribute: undefined,

    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    agreeDeleteButtonLoading: false,
    isAddSubfeatureDialogVisible: false,
    isAddSubfeatureDialogLoaded: false,
    addSubfeatureButtonLoading: false,
    addedObject: {
      type: undefined,
      datasource: undefined,
      id: undefined,
    },
  }),
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    isNewCard: sync('map/isNewCard'),
    permissions: get('users/layersPermissions'),
    layers: get('layers/layers'),
    relationValuesMapping: get('layers/relationValuesMapping'),

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    isEditingButtonActive: sync('edit/isEditingButtonActive'),

    geometryPropertyName: get('layers/metadata@:zdmStreetLampDataSourceName.attributes_schema.geometry_name'),
    dataSourcesAll: get('layers/metadata'),
    headCatalogDatasourceName: get('admin/modulesMapping@zdm_data.catalogs.head.datasource'),
    zdmDataDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    zdmStreetLampDataSourceName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.datasource'),
    poleDataSourceName: get('admin/modulesMapping@zdm_data.datasources.pole.datasource'),
    pickArmDataSourceName: get('admin/modulesMapping@zdm_data.datasources.pick_arm.datasource'),
    headDataSourceName: get('admin/modulesMapping@zdm_data.datasources.head.datasource'),

    streetLampsIdPropName: get('layers/metadata@:zdmStreetLampDataSourceName.attributes_schema.id_name'),
    poleIdPropName: get('layers/metadata@:poleDataSourceName.attributes_schema.id_name'),
    pickArmsIdPropName: get('layers/metadata@:pickArmDataSourceName.attributes_schema.id_name'),
    lightHeadsIdPropName: get('layers/metadata@:headDataSourceName.attributes_schema.id_name'),

    zdmDataLayers: get('admin/modulesMapping@zdm_data.layers'),
    streetLampsLayerId: get('admin/modulesMapping@zdm_data.layers.street_lamp'),
    polesLayerId: get('admin/modulesMapping@zdm_data.layers.pole'),
    pickArmsLayerId: get('admin/modulesMapping@zdm_data.layers.pick_arm'),
    lightHeadsLayerId: get('admin/modulesMapping@zdm_data.layers.head'),

    zdmStreetLampDisplayedName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.display_attribute_name'),
    poleDisplayedName: get('admin/modulesMapping@zdm_data.datasources.pole.display_attribute_name'),
    pickArmDisplayedName: get('admin/modulesMapping@zdm_data.datasources.pick_arm.display_attribute_name'),
    headDisplayedName: get('admin/modulesMapping@zdm_data.datasources.head.display_attribute_name'),

    zdmStreetLampCatalogAttributeName: get(
      'admin/modulesMapping@zdm_data.datasources.street_lamp.catalog_attribute_name'
    ),
    poleCatalogAttributeName: get('admin/modulesMapping@zdm_data.datasources.pole.catalog_attribute_name'),
    pickArmCatalogAttributeName: get('admin/modulesMapping@zdm_data.datasources.pick_arm.catalog_attribute_name'),
    headCatalogAttributeName: get('admin/modulesMapping@zdm_data.datasources.head.catalog_attribute_name'),
    isDatasourceConnectedToExternalSystem: get('externalSystems/connectedDataSources@:dataSource'),
    isExternalSystemsEnabled: get('admin/settingsValues@external_systems_module_enabled'),
    datasourceSystemId() {
      return this.isDatasourceConnectedToExternalSystem?.id;
    },

    datasources() {
      return [
        this.zdmStreetLampDataSourceName,
        this.headDataSourceName,
        this.pickArmDataSourceName,
        this.poleDataSourceName,
      ];
    },

    layersIdDict() {
      return {
        [this.zdmStreetLampDataSourceName]: this.streetLampsLayerId,
        [this.poleDataSourceName]: this.polesLayerId,
        [this.pickArmDataSourceName]: this.pickArmsLayerId,
        [this.headDataSourceName]: this.lightHeadsLayerId,
      };
    },
    idPropNameDict() {
      return {
        [this.zdmStreetLampDataSourceName]: this.streetLampsIdPropName,
        [this.poleDataSourceName]: this.poleIdPropName,
        [this.pickArmDataSourceName]: this.pickArmsIdPropName,
        [this.headDataSourceName]: this.lightHeadsIdPropName,
      };
    },

    displayNameDict() {
      return {
        [this.zdmStreetLampDataSourceName]: this.zdmStreetLampDisplayedName,
        [this.poleDataSourceName]: this.poleDisplayedName,
        [this.pickArmDataSourceName]: this.pickArmDisplayedName,
        [this.headDataSourceName]: this.headDisplayedName,
      };
    },

    catalogAttributeNameDict() {
      return {
        [this.zdmStreetLampDataSourceName]: this.zdmStreetLampCatalogAttributeName,
        [this.poleDataSourceName]: this.poleCatalogAttributeName,
        [this.pickArmDataSourceName]: this.pickArmCatalogAttributeName,
        [this.headDataSourceName]: this.headCatalogAttributeName,
      };
    },
    extrasPermission() {
      return this.permissions[this.layerId].attachments_and_comments_value;
    },
    title() {
      return {
        title: this.feature.properties[this.displayNameDict[this.zdmStreetLampDataSourceName]],
        subtitle: this.$i18n.t('sidebar.zdmLamp.street_lamp'),
      };
    },
    layerId() {
      return this.layer.id;
    },
    featureId() {
      return this.lampId || this.routeParams.fid;
    },
    subFeatureId() {
      return this.$route.query?.id || null;
    },
    subFeatureType() {
      return this.$route.query?.type || MAIN_TYPE;
    },
    subFeatureLayerId() {
      return this.zdmDataLayers[this.subFeatureType];
    },
    subFeatureDataSourceName() {
      return this.zdmDataDataSources?.[this.subFeatureType]?.datasource || '';
    },
    routeParams() {
      return this.$route.params;
    },
    routeQuery() {
      return this.$route.query;
    },
    deleteDialogBody() {
      return this.$i18n.t('dialog.body.deleteObject', { id: this.deletedId });
    },
    deleteDialogTitle() {
      return this.$i18n.t('dialog.title.deleteObject');
    },

    addSubfeatureDialogTitle() {
      return this.$i18n.t(
        `dialog.title.${this.addedObject.addedDatasource === 'zdm_data_head' ? 'addHead' : 'addPickArm'}`
      );
    },
    isLightingControlAdminEnabled: get('admin/settingsValues@lighting_control_module_enabled'),
    isLightingControlSuperAdminEnabled: get('admin/additionalModules@LIGHTING_CONTROL.enabled'),
    isLightingControlModuleEnabled() {
      return this.isLightingControlAdminEnabled && this.isLightingControlSuperAdminEnabled;
    },
    numberAttributeName() {
      return this.$_objectToArray(this.zdmDataSources).find(d => d.datasource === this.zdmStreetLampDataSourceName)
        ?.display_attribute_name;
    },
    streetAttributeName() {
      return this.$_objectToArray(this.zdmDataSources).find(d => d.datasource === this.zdmStreetLampDataSourceName)
        ?.street_attribute_name;
    },
    districtAttributeName() {
      return this.$_objectToArray(this.zdmDataSources).find(d => d.datasource === this.zdmStreetLampDataSourceName)
        ?.district_attribute_name;
    },
    numberAttributeValue() {
      return this.subfeaturesDict?.[`${MAIN_TYPE}@${this.featureId}`]?.[this.numberAttributeName];
    },
    streetAttributeValue() {
      return this.subfeaturesDict?.[`${MAIN_TYPE}@${this.featureId}`]?.[this.streetAttributeName];
    },
    districtAttributeValue() {
      return this.subfeaturesDict?.[`${MAIN_TYPE}@${this.featureId}`]?.[this.districtAttributeName];
    },
  },
  watch: {
    isFormValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
  },
  methods: {
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getLampWithObjectsAction: call('zdm/getZdmObjectTreeStructure'),
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    checkDatasourceConnection: call('externalSystems/checkDatasourceConnection'),
    openZSIDialog() {
      this.isZSIDialogLoaded = true;
      this.isZSIDialogVisible = true;
    },
    async editNonTopoFeature() {
      try {
        this.isStickyButtonsLoading = true;
        const { type, currentObjectId: id } = this;
        await this.editNonTopoFeatureAction({
          datasource: this.getDatasource(type),
          id,
          body: this.getPayload(type),
          params: { layer_id: this.zdmDataLayers[type] },
        });
        this.stopEditing();
        this.refreshLayersAndTable([this.layerId]);
        await this.init(this.featureId, true);
        this.isStickyButtonsLoading = false;
      } catch (error) {
        console.log(error);
        this.isStickyButtonsLoading = false;
      }
    },
    startEditing(datasource) {
      this.featureCopy = cloneDeep(this.feature);
      this.valuesCopy = cloneDeep(this.values);
      this.geometryCopy = cloneDeep(this.feature.geometry);

      this.isEditingOn = true;
      this.isStickyButtonsVisible = true;
      this.stickyButtons = [
        {
          action: 'featureEditing',
          translationPath: 'dialog.save',
        },
        {
          action: 'cancelEditing',
          translationPath: 'dialog.disagree',
          cancel: true,
        },
      ];
      this.isEditingButtonActive = true;
      if (datasource === this.zdmStreetLampDataSourceName) {
        this.$root.$emit('modifySidebarGeometry', {
          geometryValue: this.geometry,
          modifyendCallback: this.onModifyend,
          layerId: this.layerId,
        });
      }
    },
    cancelEditing() {
      this.stopEditing();
      this.feature = cloneDeep(this.featureCopy);
      this.values = cloneDeep(this.valuesCopy);
      this.geometry = cloneDeep(this.geometryCopy);
    },
    stopEditing() {
      this.isEditingOn = false;
      this.isStickyButtonsVisible = false;
      this.isStickyButtonsLoading = false;
      this.stickyButtons = [];
      this.isEditingButtonActive = false;
      this.$root.$emit('selection-action', this.tableSelectionType);
      this.$root.$emit('deleteSidebarGeometry');
    },
    setSidebarGeometry(feature) {
      if (!feature) {
        this.geometry = null;
      } else {
        this.geometry = prepareDrawnGeometry(feature);
      }
    },
    onModifyend(e) {
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    openAddDialog(id, parentDatasource) {
      const addedType = parentDatasource === this.pickArmDataSourceName ? 'head' : 'pick_arm';
      const addedDatasource = this.getDatasource(addedType);
      const layer =
        parentDatasource === this.zdmStreetLampDataSourceName
          ? this.layer
          : this.layers[this.layersIdDict[addedDatasource]];
      const layerSchema = layer.form_schema.elements;
      const catalogAttributeName =
        parentDatasource !== this.zdmStreetLampDataSourceName && this.catalogAttributeNameDict[addedDatasource];
      this.addedObjectInputConfig = {
        ...this.getTypeAttribute({
          datasource: addedDatasource,
          layerSchema,
          catalogAttributeName,
        }),
        name: 'type',
        rules: [v => !!v || v == '0' || 'rules.required'],
        cols: '12',
      };
      this.addedObject = {
        id,
        parentDatasource,
        addedDatasource,
      };
      this.isAddSubfeatureDialogVisible = true;
      this.isAddSubfeatureDialogLoaded = true;
    },
    getParentIdKey(datasource) {
      return this.idPropNameDict[datasource];
    },
    async handleSubfeatureAdding() {
      try {
        this.addSubfeatureButtonLoading = true;
        const { id, type, addedDatasource } = this.addedObject;
        const catalogAttributeName = this.catalogAttributeNameDict[addedDatasource];
        const payload =
          addedDatasource === 'zdm_data_pick_arm'
            ? { 'zdm_data_pick_arm.pole_id': id, [catalogAttributeName]: type, 'zdm_data_pick_arm.main': false }
            : { 'zdm_data_head.pick_arm_id': id, [catalogAttributeName]: type };
        await this.addNonTopoFeatureAction({
          datasource: addedDatasource,
          body: payload,
          params: {
            layer_id: this.zdmDataLayers[type],
          },
        });
        await this.init(this.featureId, true);
        this.addSubfeatureButtonLoading = false;
        this.isAddSubfeatureDialogVisible = false;
        this.addedObject.type = undefined;
      } catch (error) {
        this.addedObject.type = undefined;
        this.isAddSubfeatureDialogVisible = false;
        this.addSubfeatureButtonLoading = false;
        console.log('  error', error);
      }
    },
    pushToParentRoute() {
      this.$router.push({
        name: this.$route.meta.parent,
        params: {
          ...this.$route.params,
          lid: this.layerId,
        },
      });
      this.currentTab = 0;
    },
    refreshLayersAndTable(layers) {
      this.$root.$emit('refreshTableData');
      this.$root.$emit(
        'refreshMvtSources',
        layers.map(layer => ({ id: layer, group: 'layers' })),
        true
      );
      this.$root.$emit('updateLegendStylesCounts', { layersIds: layers });
    },
    onDelete(id, datasource, layerId) {
      this.deletedId = id;
      this.deletedDatasource = datasource;
      this.deletedLayerId = layerId;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    async deleteObjectHandler() {
      try {
        this.agreeDeleteButtonLoading = true;
        const { deletedDatasource, deletedId, deletedLayerId } = this;
        await this.deleteDatasourceFeature({
          datasource: deletedDatasource,
          id: deletedId,
          params: { layer_id: deletedLayerId },
        });
        if (this.zdmStreetLampDataSourceName === deletedDatasource) {
          this.pushToParentRoute();
          this.refreshLayersAndTable([this.layerId]);
        } else {
          this.activeFeature = `${MAIN_TYPE}@${this.featureId}`;
          await this.init(this.featureId, true);
        }
        this.agreeDeleteButtonLoading = false;
        this.isDeleteDialogVisible = false;
      } catch (error) {
        console.log('  error', error);
        this.isDeleteDialogVisible = false;
        this.agreeDeleteButtonLoading = false;
      }
    },
    goBack() {
      this.$router.back();
    },
    setFormSchema(layerSchema, metadata, filteredOutAttributes) {
      this.formSchema = this.getForm(layerSchema, metadata, { filteredOutAttributes });
    },
    setValues(attributesValues) {
      this.values = this.getValues(this.formSchema, attributesValues);
    },
    setSubfeaturesDict(lamp) {
      this.subfeaturesDict = this.getSubfeaturesDict(lamp, MAIN_TYPE);
    },
    getSubfeaturesDict(object, type) {
      return object.reduce((total, current) => {
        const subfeatureKey = this.subFeaturesNamesDict[type];
        const treeviewId = `${type}@${current.id}`;
        const currentObject = {
          ...current,
          [this.idPropNameDict[this.getDatasource(type)]]: current.id,
          treeviewId,
          type,
          ...current.properties,
        };
        delete currentObject.properties;
        return {
          ...total,
          [treeviewId]: currentObject,
          ...(subfeatureKey ? this.getSubfeaturesDict(current[subfeatureKey], subfeatureKey) : {}),
        };
      }, {});
    },
    setTreeviewStructure(lamp) {
      this.treeviewStructure = this.getTreeviewStructure(lamp, MAIN_TYPE);
    },
    getAdditionalTreeviewProperties(type, datasource, properties) {
      if (type === MAIN_TYPE) return;
      const defaultNameChip = {
        value: properties[this.displayNameDict[datasource]],
        displayName: this.dataSourcesAll[datasource]?.attributes_schema.attributes.find(
          attr => attr.name === this.zdmDataDataSources[type]?.display_attribute_name
        )?.verbose_name,
      };
      switch (type) {
        case 'head':
          return [
            ...(properties[`${this.headCatalogDatasourceName}.total_power`]
              ? [
                  {
                    value: `${properties[`${this.headCatalogDatasourceName}.total_power`]} W`,
                    displayName: this.dataSourcesAll[this.headCatalogDatasourceName]?.attributes_schema.attributes.find(
                      attr => attr.name === 'total_power'
                    )?.verbose_name,
                  },
                ]
              : []),
            defaultNameChip,
          ];
        default:
          return [defaultNameChip];
      }
    },
    getTreeviewStructure(object, type) {
      return object.reduce((total, current) => {
        const subfeatureKey = this.subFeaturesNamesDict[type];
        const datasource = this.getDatasource(type);
        const { id, properties, main } = current;
        const layerId = this.zdmDataLayers[type];
        const children = subfeatureKey ? this.getTreeviewStructure(current[subfeatureKey], subfeatureKey) : undefined;
        return [
          ...total,
          {
            id,
            type,
            datasource,
            layerId,
            additionalPropertiesList: this.getAdditionalTreeviewProperties(type, datasource, properties),
            treeviewId: `${type}@${id}`,
            translatedType: this.$i18n.t(`sidebar.zdmLamp.${type}`),
            dotsItems: this.getDotsItems(type, children, main),
            isMain: main || false,
            children,
          },
        ];
      }, []);
    },
    getDotsItems(type, children, isMain) {
      const isPickArm = type === 'pick_arm';
      return [
        {
          name: 'edit',
        },
        ...this.getDotsAddItem(type, children?.length, isMain),
        {
          name: 'delete',
          disabled: isPickArm && isMain,
          tooltip: isPickArm && isMain ? this.$i18n.t('sidebar.zdmLamp.mainPickArmDelete') : undefined,
        },
      ];
    },
    getDotsAddItem(type, amount) {
      if (type === 'pole') {
        const hasMoreThanOnePickArm = amount > 1;
        return [
          {
            name: 'addPickArm',
            disabled: hasMoreThanOnePickArm,
            tooltip: hasMoreThanOnePickArm ? this.$i18n.t('sidebar.zdmLamp.maxTwoPickArms') : undefined,
          },
        ];
      }
      if (type === 'pick_arm') {
        return [
          {
            name: 'addHead',
          },
        ];
      }
      return [];
    },
    setActiveFeature({ type, id }) {
      if (type && id) {
        this.activeFeature = `${type}@${id}`;
      } else {
        this.activeFeature = `${MAIN_TYPE}@${this.featureId}`;
      }
    },
    getDatasource(type) {
      return `zdm_data_${type}`;
    },
    attachListeners() {
      this.unwatchFeatureId = this.$watch('featureId', async nV => {
        this.unwatchActiveFeature?.();
        await this.init(nV, true);
        this.watchActiveFeature();
      });
      this.watchActiveFeature();
    },
    watchActiveFeature() {
      this.unwatchActiveFeature = this.$watch('activeFeature', nV => {
        const [type, id] = nV.split('@');
        this.setActiveFeatureData(type, id);
        this.$router.push({
          ...this.$route,
          query: {
            ...this.routeQuery,
            type,
            id,
          },
        });
      });
    },
    getAttributesMetadata(datasource) {
      return this.metadata[datasource].attributes_schema.attributes;
    },
    setActiveFeatureData(type, id) {
      this.type = type;
      this.currentObjectId = id;
      const datasource = this.getDatasource(type);
      const attributesMetadata = this.getAttributesMetadata(datasource);
      const currentFeature = this.subfeaturesDict[`${type}@${id}`];
      const layer = this.layers[this.layersIdDict[datasource]];
      const layerSchema = layer.form_schema.elements;
      const catalogAttributeName =
        datasource !== this.zdmStreetLampDataSourceName && this.catalogAttributeNameDict[datasource];
      const filteredAttibutes = catalogAttributeName ? [catalogAttributeName] : undefined;
      this.setFormSchema(layerSchema, attributesMetadata, filteredAttibutes);
      this.setValues(currentFeature);
      if (datasource !== this.zdmStreetLampDataSourceName) {
        this.setTypeAttribute({ datasource, layerSchema, catalogAttributeName });
        this.setTypeValue(currentFeature, catalogAttributeName);
      }
    },
    getTypeAttribute({ datasource, layerSchema, catalogAttributeName }) {
      const typeElement = this.$_findObjectInNestedArraysByValue(
        layerSchema,
        item => item.attribute === catalogAttributeName,
        'elements'
      );
      if (!typeElement) {
        return;
      }
      const preapredAttribute = this.getForm([typeElement], this.getAttributesMetadata(datasource))[0];
      delete preapredAttribute.label;
      return {
        ...preapredAttribute,
        name: catalogAttributeName,
        translationPath: 'sidebar.type',
        outlined: true,
        dense: true,
        'align-items': 'center',
        icon: 'mdi-tag-multiple',
        hasDivider: true,
      };
    },
    setTypeAttribute({ datasource, layerSchema, catalogAttributeName }) {
      this.typeAttribute = this.getTypeAttribute({ datasource, layerSchema, catalogAttributeName });
    },
    setTypeValue(currentFeature, catalogAttributeName) {
      this.values = {
        ...this.values,
        [catalogAttributeName]: currentFeature[catalogAttributeName],
      };
    },
    onMounted() {
      this.init(this.featureId);
    },
    toggleListeners(isOn) {
      const method = isOn ? '$on' : '$off';
      this.$root[method]('featureEditing', this.editNonTopoFeature);
      this.$root[method]('cancelEditing', this.cancelEditing);
      this.$root[method]('turnOffEditing', this.stopEditing);
      this.$root[method]('tableDataFetched', this.init);
      this.$root[method]('refreshFormData', this.refreshFormData);
      this.$root[method]('turnOnEditing', () => {
        if (!this.activeFeature) return;
        const dS = this.getDatasource(this.activeFeature.split('@')[0]);
        if (dS) this.startEditing(dS);
      });
    },
    getPayload(type) {
      return {
        ...this.values,
        ...(type === MAIN_TYPE ? { [this.geometryPropertyName]: this.geometry } : {}),
      };
    },
    refreshTableAndMap() {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshMvtSource', this.layerId);
      this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.layerId] });
    },
    async fetchLampStructure(id) {
      const r = await this.getLampWithObjectsAction({ id, datasource: this.zdmStreetLampDataSourceName });
      this.setSubfeaturesDict([r.data.data]);
      this.setTreeviewStructure([r.data.data]);
      this.feature = {
        ...r.data.data,
        [this.idPropNameDict[this.zdmStreetLampDataSourceName]]: r.data.data.id,
      };
      this.geometry = r.data.data.geometry;
      if (this.isEditingButtonActive) {
        this.startEditing(this.zdmStreetLampDataSourceName);
      }
    },
    refreshFormData() {
      this.init(this.featureId, true);
    },
    async init(id = this.featureId, isSkipMetadataFetching) {
      if (!this.mapPanelDataFetched) {
        return;
      }
      try {
        this.clearHighlitedFeature(this.layerId);
        this.isLoading = true;
        await Promise.all([
          this.fetchLampStructure(id),
          ...(isSkipMetadataFetching
            ? []
            : this.datasources.map(
                async datasource =>
                  await this.fetchData(datasource, this.layers[this.layersIdDict[datasource]].form_schema.elements)
              )),
        ]);
        const type = this.routeQuery?.type || MAIN_TYPE;
        const objectId = this.routeQuery?.id || id;
        this.type = type;
        this.currentObjectId = objectId;
        this.setActiveFeatureData(type, objectId);
        this.highlightFeature(this.layerId, this.isSubfeature ? +this.routeQuery?.id : this.feature.id);
        this.setActiveFeature(this.routeQuery);
        if (!isSkipMetadataFetching) {
          this.attachListeners();
        }
        this.isLoading = false;
        if (this.isNewCard) {
          this.isNewCard = false;
          const { crs } = this.geometry;
          this.$root.$emit('createGeomAndFitView', { ...this.geometry, crs });
        }
      } catch (error) {
        this.$router.push({ name: 'layerTable', params: { lid: this.$route.params.lid } });
        throw error;
      }
    },
  },
  mounted() {
    this.onMounted();
    if (this.isExternalSystemsEnabled) this.checkDatasourceConnection(this.dataSource);
    this.toggleListeners(true);
    this.$once('hook:beforeDestroy', () => {
      this.clearHighlitedFeature(this.layerId);
      this.unwatchFeatureId?.();
      this.unwatchActiveFeature?.();
      this.toggleListeners(false);
    });
  },
};
</script>

<style lang="scss" scoped></style>
